import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChatFlowCaption from 'components/@home/@settings/Tabs/ChatFlowCaption';
import chatFlowEntityTypeFormik from 'components/@home/@settings/Tabs/chatFlowEntityTypeFormik';
import connector from 'components/@home/@settings/Tabs/connector';
import { handleKeyDown } from 'components/@home/@settings/Tabs/handleKeyDown';
import withContext from 'components/@home/@settings/Tabs/withContext';
import FormikChipInput from 'components/formik/ChipInput';
import TextField from 'components/formik/TextField';
import { ErrorMessage, Field, FieldArray } from 'formik';
import isEqual from 'lodash/isEqual';
import ArrowBackIcon from 'mdi-react/ArrowBackIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import DeleteIcon from 'mdi-react/DeleteIcon';
import { func, object } from 'prop-types';
import React, { memo, useMemo } from 'react';
import { I18n } from 'utils/i18n';

const ENTITY_CELL_CLASS = 'chat-flow-entity-cell';

const getStyles = theme => ({
  root: {
    flexGrow: 1,
    display: 'flex',
    backgroundColor: theme.palette.background.paper,
    flexDirection: 'column',
    borderLeft: `solid 1px ${theme.palette.secondary.extraLight}`,
  },
  title: {
    fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 'normal',
    fontStyle: 'normal',
    fontStretch: 'normal',
    color: theme.palette.primary.ultraDark,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  close: {
    color: theme.palette.primary.ultraDark,
  },
  body: {
    flexGrow: 1,
    overflow: 'auto',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(3),
  },
  footer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  field: {
    marginTop: theme.spacing(2),
  },
  table: {
    marginTop: theme.spacing(3),
    tableLayout: 'fixed',
    borderSpacing: '10px',
    borderCollapse: 'separate',
  },
  withActions: {
    display: 'flex',
    alignItems: 'center',
  },
});

const ChatFlowEntityType = ({
  context,
  setFieldValue,
  handleSubmit,
  values: { options: optionsIn },
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const options = [...optionsIn];
  if (options.length === 0 || options[options.length - 1].name !== '') {
    setFieldValue('options', [...options, { _id: `${Math.random()}`, name: '', texts: [] }]);
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={styles.root}>
      <Box component="header" sx={styles.header}>
        <Box sx={styles.headerLeft}>
          {context.length > 1 && (
            <IconButton
              sx={styles.close}
              edge="start"
              aria-label="close"
              onClick={context.pop}
              size="large"
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography size="small" sx={styles.title}>
            {I18n.t('ChatFlowSettings.Entity type')}
          </Typography>
        </Box>
        <div>
          <IconButton
            sx={styles.close}
            edge="end"
            aria-label="close"
            onClick={context.clear}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </div>
      </Box>
      <Box component="section" sx={styles.body}>
        <ChatFlowCaption section="EntityType" />
        <Typography sx={styles.heading}>{I18n.t('ChatFlowSettings.Display name')}</Typography>
        <Field
          sx={styles.field}
          component={TextField}
          name="name"
          placeholder={I18n.t('ChatFlowSettings.Display name')}
        />
        <Typography sx={styles.heading}>{I18n.t('ChatFlowSettings.Options')}</Typography>
        <ChatFlowCaption section="Options" />
        <Table size="small" padding="none" sx={styles.table}>
          <TableHead>
            <TableRow hover={false}>
              <TableCell>{I18n.t('ChatFlowSettings.Name')}</TableCell>
              <TableCell>{I18n.t('ChatFlowSettings.Texts')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <FieldArray
              name="options"
              render={arrayHelpers =>
                options.map((p, idx) => (
                  <TableRow key={p._id}>
                    <TableCell className={ENTITY_CELL_CLASS}>
                      <Field
                        component={TextField}
                        name={`options.${idx}.name`}
                        id={`options.${p._id}.name`}
                        placeholder={I18n.t('ChatFlowSettings.Option name')}
                        onKeyDown={handleKeyDown(
                          idx,
                          `options.${idx}.name`,
                          options[idx].name,
                          setFieldValue,
                          ENTITY_CELL_CLASS,
                        )}
                        onBlur={() => {
                          if (
                            options[idx].name &&
                            !options[idx].texts.includes(options[idx].name)
                          ) {
                            setFieldValue(`options.${idx}.texts`, [
                              options[idx].name,
                              ...options[idx].texts,
                            ]);
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={styles.withActions}>
                      <Field
                        component={FormikChipInput}
                        placeholder={I18n.t('ChatFlowSettings.Add synonyms')}
                        name={`options.${idx}.texts`}
                        creatable
                      />
                      <IconButton
                        size="small"
                        edge="end"
                        aria-label="delete"
                        onClick={() => arrayHelpers.remove(idx)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              }
            />
            <ErrorMessage name="options">
              {msg => (
                <TableRow>
                  <TableCell>
                    <FormHelperText error>{msg}</FormHelperText>
                  </TableCell>
                </TableRow>
              )}
            </ErrorMessage>
          </TableBody>
        </Table>
      </Box>
      <Box component="footer" sx={styles.footer}>
        <div />
        <Button variant="contained" color="primary" type="submit">
          {I18n.t('ChatFlowSettings.Save')}
        </Button>
      </Box>
    </Box>
  );
};

ChatFlowEntityType.propTypes = {
  values: object.isRequired,
  handleSubmit: func.isRequired,
  context: object.isRequired,
  setFieldValue: func.isRequired,
};

const areEqual = (prev, next) => {
  return prev.entityType?._id === next.entityType?._id && isEqual(prev.values, next.values);
};
export default connector(withContext(memo(chatFlowEntityTypeFormik(ChatFlowEntityType), areEqual)));
